import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, Sparkles, Globe, Lightbulb } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Définition des domaines KG Technologies
const kgDomains = [
  {
    name: 'Solutions',
    description: 'Solutions technologiques innovantes',
    path: '/',  // Landing page des solutions
    color: 'from-emerald-400 to-teal-400',
    icon: Sparkles,
    bgGlow: 'from-emerald-600/20 to-teal-600/20',
    accentColor: 'emerald'
  },
  {
    name: 'Academy',
    description: 'Formation finance & technologie',
    path: '/academy',
    color: 'from-[#017D7E] to-[#00A99D]',
    icon: Globe,
    bgGlow: 'from-green-600/20 to-emerald-600/20',
    accentColor: 'blue'
  },
  {
    name: 'Services',
    description: 'Services professionnels',
    path: '/services',
    color: 'from-violet-400 to-purple-400',
    icon: Lightbulb,
    bgGlow: 'from-violet-600/20 to-purple-600/20',
    accentColor: 'violet'
  }
];

// Définition des liens de navigation
const navigationLinks = [
  {
    title: 'Solutions',
    links: [
      { name: 'Catalogue des solutions', path: '/portfolio' },
      { name: 'Factory', path: '/factory' },
      { name: 'Integration', path: '/integration' },
      { name: 'Consulting', path: '/consulting' }
    ]
  },
  {
    title: 'Academy',
    links: [
      { name: 'Catalogue des formations', path: '/academy/catalog' },
      { name: 'Corporate', path: '/academy/corporate' },
      { name: 'Sur-mesure', path: '/academy/custom' },
      { name: 'Abonnements', path: '/academy/subscription' }
    ]
  },
  {
    title: 'Services',
    links: [
      
      { name: 'AMOA & Conseil', path: '/services/amoa' },
      { name: 'Support Applicatif', path: '/services/support' },
      { name: 'Audit des SI', path: '/services/audit' }
    ]
  },
  { title: 'FinTech Lab', path: '/rd-showcase' },
  { title: 'À propos', path: '/about' },
  { title: 'Contact', path: '/contact' }
];

// Styles pour les animations
const styles = `
  @keyframes float {
    0% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
    100% { transform: translateY(0px); }
  }

  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }

  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }

  .domain-card-hover {
    transition: all 0.3s ease;
  }

  .domain-card-hover:hover {
    transform: translateY(-5px) scale(1.02);
  }

  .shimmer {
    background: linear-gradient(
      90deg,
      rgba(255,255,255,0) 0%,
      rgba(255,255,255,0.1) 50%,
      rgba(255,255,255,0) 100%
    );
    background-size: 1000px 100%;
    animation: shimmer 2s infinite linear;
  }

  .floating {
    animation: float 6s ease-in-out infinite;
  }

  .pulse {
    animation: pulse 2s ease-in-out infinite;
  }
`;

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);

    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      styleSheet.remove();
    };
  }, []);

  const handleNavigate = (path) => {
    setIsOpen(false);
    setActiveDropdown(null);
    navigate(path);
  };

  // Composant DropdownMenu
  const DropdownMenu = ({ items, title }) => (
    <div className="relative group">
      <button className="flex items-center gap-2 text-white/70 hover:text-white transition-colors group">
        <span>{title}</span>
        <ChevronDown className="w-4 h-4 transform group-hover:rotate-180 transition-transform duration-300" />
      </button>

      <div className="absolute top-full left-0 min-w-[200px] hidden group-hover:block pt-4">
        <div className="bg-[#003B3B]/80 backdrop-blur-xl rounded-xl border border-white/10 overflow-hidden
                     shadow-xl shadow-black/50">
          <div className="py-2">
            {items.map((item, index) => (
              <button
                key={index}
                onClick={() => handleNavigate(item.path)}
                className="w-full text-left px-4 py-2 text-white/70 hover:text-white hover:bg-white/5
                          transition-colors duration-200 flex items-center gap-2"
              >
                {item.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Composant DomainSelector
  const DomainSelector = () => (
    <div className="absolute top-full left-0 hidden group-hover:block pt-4"
         style={{ minWidth: '600px' }}>
      <div className="bg-[#003B3B]/80 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden
                    shadow-2xl shadow-black/50 p-4">
        <div className="text-center mb-6 px-4">
          <h3 className="text-white/90 text-lg font-medium mb-2">Nos Domaines</h3>
          <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        <div className="grid grid-cols-3 gap-4">
          {kgDomains.map((domain, index) => (
            <button
              key={index}
              onClick={() => handleNavigate(domain.path)}
              className="group/card domain-card-hover"
            >
              <div className="relative bg-white/5 rounded-xl p-4 h-full border border-white/5
                          hover:border-white/20 transition-all duration-300">
                <div className={`absolute inset-0 bg-gradient-to-br ${domain.bgGlow} opacity-0
                              group-hover/card:opacity-100 transition-opacity duration-300 rounded-xl blur-lg`} />
                
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 rounded-lg bg-gradient-to-br ${domain.color} bg-opacity-10
                                  group-hover/card:bg-opacity-20 transition-all duration-300`}>
                      <domain.icon className="w-5 h-5 text-white" />
                    </div>
                    <h4 className={`text-transparent bg-clip-text bg-gradient-to-r ${domain.color} 
                                font-semibold text-lg`}>
                      {domain.name}
                    </h4>
                  </div>

                  <p className="text-white/60 text-sm mb-4 group-hover/card:text-white/80 transition-colors">
                    {domain.description}
                  </p>

                  <div className="flex items-center gap-2 text-sm">
                    <span className={`text-${domain.accentColor}-400/70 
                                  group-hover/card:text-${domain.accentColor}-400 transition-colors`}>
                      Découvrir
                    </span>
                  </div>

                  <div className="absolute inset-0 rounded-xl opacity-0 group-hover/card:opacity-100
                              transition-opacity duration-300 shimmer pointer-events-none" />
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-white/5 text-center">
          <p className="text-xs text-white/40">
            Explorez nos univers KG Technologies
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled ? 'bg-[#050A1C]/90 backdrop-blur-xl shadow-2xl shadow-black/20' : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-4">
        <div className="relative flex items-center justify-between py-4">
          {/* Logo et sélecteur de domaines */}
          <div className="relative group">
            <button className="relative z-10 group-hover:text-[#017D7E] transition-colors flex items-center gap-2">
              <span className="text-white text-xl font-bold">
                KG Technologies
              </span>
              <ChevronDown className="w-4 h-4 transform group-hover:rotate-180 transition-transform duration-300" />
            </button>
            <DomainSelector />
          </div>

          {/* Navigation Desktop */}
          <div className="hidden md:flex items-center gap-8">
            {navigationLinks.map((item, index) => (
              item.links ? (
                <DropdownMenu key={index} title={item.title} items={item.links} />
              ) : (
                <button
                  key={index}
                  onClick={() => handleNavigate(item.path)}
                  className="text-white/70 hover:text-white transition-colors relative group"
                >
                  {item.title}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#017D7E] to-[#00A99D]
                                group-hover:w-full transition-all duration-300" />
                </button>
              )
            ))}
          </div>

          {/* Menu Mobile Button */}
          <button
            className="md:hidden relative group p-2 rounded-lg border border-white/10
                     hover:border-green-400/50 transition-all duration-300"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? (
              <X className="h-6 w-6 text-white" />
            ) : (
              <Menu className="h-6 w-6 text-white" />
            )}
          </button>
        </div>

        {/* Menu Mobile Overlay */}
        <div className={`md:hidden fixed inset-0 bg-[#050A1C]/98 backdrop-blur-xl transition-all duration-500
                      ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`}>
          <div className="container mx-auto px-4 py-8">
            {/* Version mobile des domaines */}
            <div className="mb-8">
              <div className="text-lg font-medium text-white/90 mb-4">Nos Domaines</div>
              <div className="space-y-4">
                {kgDomains.map((domain, index) => (
                  <button
                    key={index}
                    onClick={() => handleNavigate(domain.path)}
                    className="flex items-start gap-4 p-4 w-full text-left rounded-xl bg-white/5 border border-white/10
                             hover:border-white/20 transition-all duration-300"
                  >
                    <div className={`p-2 rounded-lg bg-gradient-to-br ${domain.color} bg-opacity-10`}>
                      <domain.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className={`text-transparent bg-clip-text bg-gradient-to-r ${domain.color} 
                                    font-medium mb-1`}>
                        {domain.name}
                      </div>
                      <p className="text-sm text-white/60">{domain.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation mobile */}
            <div className="space-y-6">
              {navigationLinks.map((item, index) => (
                <div key={index}>
                  {item.links ? (
                    <div>
                      <button
                        onClick={() => setActiveDropdown(activeDropdown === index ? null : index)}
                        className="flex items-center justify-between w-full text-white/90 mb-2"
                      >
                        <span>{item.title}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 
                          ${activeDropdown === index ? 'rotate-180' : ''}`} />
                      </button>
                      
                      <div className={`space-y-2 pl-4 ${activeDropdown === index ? 'block' : 'hidden'}`}>
                        {item.links.map((subItem, subIndex) => (
                          <button
                            key={subIndex}
                            onClick={() => handleNavigate(subItem.path)}
                            className="w-full text-left py-2 text-white/60 hover:text-white
                                   transition-colors"
                          >
                            {subItem.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleNavigate(item.path)}
                      className="text-white/90 hover:text-white transition-colors"
                    >
                      {item.title}
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Mobile menu footer */}
            <div className="mt-8 pt-8 border-t border-white/10">
              <div className="text-center">
                <p className="text-sm text-white/40">
                  © 2024 KG Technologies. Tous droits réservés.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;